import React, { useState } from 'react';
import { Table } from 'semantic-ui-react';
import { useData } from '../context/DataContext';

const parseScore = (val) => {
  if (!val) return null;
  const n = parseFloat(val.toString().replace(',', '.'));
  return isNaN(n) ? null : n;
};

const MEMBERS_PER_PAGE = 10;

const CategoryStatsPage = () => {
  const { data, isLoading, error } = useData();

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const handleCategoryClick = (cat) => {
    setSelectedCategory(cat);
    setCurrentPage(1);
    setIsModalOpen(true);
  };

  const getCategoryMembers = (cat) => data.filter(item => item['Catégorie'] === cat);

  const getCategoryRanking = (members) => {
    const total = members.length;
    return members.map((m, i) => ({
      ...m,
      CategoryRank: `${i + 1} sur ${total}`,
    }));
  };

  const paginatedMembers = (members) => {
    const start = (currentPage - 1) * MEMBERS_PER_PAGE;
    return members.slice(start, start + MEMBERS_PER_PAGE);
  };

  const lastRank = data.length;

  const totalPages = selectedCategory
    ? Math.ceil(getCategoryMembers(selectedCategory).length / MEMBERS_PER_PAGE)
    : 1;

  if (isLoading) {
    return <div className="container mt-5 text-center premium-loading">Chargement des données...</div>;
  }

  if (error) {
    return <div className="container mt-5 text-center premium-error">{error}</div>;
  }

  const statsByCategory = {};
  data.forEach((m) => {
    const cat = m['Catégorie'] || 'Non renseigné';
    if (!statsByCategory[cat]) {
      statsByCategory[cat] = { count: 0, medals: 0, bestScore: null, bestSkater: null };
    }
    statsByCategory[cat].count += 1;
    if (m['Méd.'] && m['Méd.'].trim() !== '') statsByCategory[cat].medals += 1;

    const score = parseScore(m['Season Best'] || m['Score Best']);
    if (score !== null && (statsByCategory[cat].bestScore === null || score > statsByCategory[cat].bestScore)) {
      statsByCategory[cat].bestScore = score;
      statsByCategory[cat].bestSkater = `${m['Nom']} ${m['Prénom']}`;
    }
  });

  const categories = Object.keys(statsByCategory).sort();

  return (
    <div className="container mt-5">
      <h3 className="text-center mb-4 premium-section-title">Statistiques par catégorie</h3>

      {categories.length === 0 ? (
        <p className="text-center text-muted">Aucune donnée disponible pour cette saison/genre.</p>
      ) : (
        <Table celled compact className="premium-table mb-5">
          <Table.Header>
            <Table.Row className="text-center">
              <Table.HeaderCell>Catégorie</Table.HeaderCell>
              <Table.HeaderCell>Nombre de patineurs</Table.HeaderCell>
              <Table.HeaderCell>Médaillés</Table.HeaderCell>
              <Table.HeaderCell>Meilleur score</Table.HeaderCell>
              <Table.HeaderCell>Détenteur</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {categories.map((cat, i) => {
              const s = statsByCategory[cat];
              return (
                <Table.Row key={i} className="text-center">
                  <Table.Cell
                    className="fw-bold text-danger"
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleCategoryClick(cat)}
                    title="Voir le classement complet de la catégorie"
                  >
                    {cat}
                  </Table.Cell>
                  <Table.Cell>{s.count}</Table.Cell>
                  <Table.Cell>{s.medals}</Table.Cell>
                  <Table.Cell>{s.bestScore ?? '—'}</Table.Cell>
                  <Table.Cell>{s.bestSkater ?? '—'}</Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table>
      )}

      {/* ====================== MODALE CLASSEMENT CATÉGORIE ====================== */}
      {isModalOpen && selectedCategory && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.65)' }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxWidth: '1150px' }}>
            <div className="modal-content">

              <div className="modal-header">
                <h5 className="modal-title">
                  Membres de la catégorie : <strong>{selectedCategory}</strong>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setIsModalOpen(false)}
                  aria-label="Close"
                ></button>
              </div>

              <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
                <table className="table table-bordered table-hover text-center align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Nom Prénom</th>
                      <th>Naissance</th>
                      <th>Place dans la catégorie</th>
                      <th>Classement général</th>
                      <th>Médaille</th>
                      <th>Club</th>
                      <th>Best Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedMembers(getCategoryRanking(getCategoryMembers(selectedCategory))).map((m, i) => (
                      <tr key={i}>
                        <td>{m['Nom']} {m['Prénom']}</td>
                        <td>{m['Naissance']}</td>
                        <td><strong>{m.CategoryRank}</strong></td>
                        <td>{m['Cl. Gal']} ème sur {lastRank}</td>
                        <td>{m['Méd.']}</td>
                        <td>{m['Club']}</td>
                        <td>{m['Season Best'] || m['Score Best'] || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-4 px-2">
                    <span className="text-muted">
                      Page <strong>{currentPage}</strong> sur {totalPages}
                    </span>

                    <div className="btn-group">
                      <button
                        className="btn btn-outline-secondary"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(p => p - 1)}
                      >
                        Précédent
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(p => p + 1)}
                      >
                        Suivant
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsModalOpen(false)}
                >
                  Fermer
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryStatsPage;
